import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, MessageCircle, ArrowDown, Leaf, Timer } from "lucide-react";
import { IMAGES, WA_MESSAGES, wa, track } from "../../data/site";
import { scrollToId } from "../../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="inicio" ref={ref} className="relative overflow-hidden pb-16 pt-[120px] lg:pb-24 lg:pt-[160px]">
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-gradient-to-br from-brand-50 to-aqua-100 blur-3xl" />
        <div className="absolute bottom-[-20%] left-[-10%] h-[380px] w-[380px] rounded-full bg-mist blur-3xl" />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-aqua/30 bg-mist px-5 py-2.5 text-sm font-semibold text-navy sm:text-base"
            data-testid="hero-badge"
          >
            <MapPin className="h-5 w-5 text-brand" />
            Atendimento ao domicílio em Lisboa e região
          </motion.div>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.2}>Devolvemos o</MaskedLine>
            <MaskedLine delay={0.32}>
              <span className="bg-gradient-to-r from-brand to-aqua bg-clip-text text-transparent">
                aspeto de novo
              </span>
            </MaskedLine>
            <MaskedLine delay={0.44}>aos seus estofos.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-500 md:text-lg"
            data-testid="hero-subheadline"
          >
            Higienização profissional de sofás, colchões, cadeiras, carpetes, cabeceiras e estofos
            automóveis em Lisboa e região.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <a
              href={wa(WA_MESSAGES.default)}
              target="_blank"
              rel="noopener noreferrer"
              id="cta-hero"
              data-testid="cta-hero-whatsapp"
              data-event="quote_request"
              onClick={() => track("quote_request", { location: "hero" })}
              className="cta-whatsapp cta-orcamento inline-flex items-center justify-center gap-2.5 rounded-full bg-brand px-7 py-4 text-base font-semibold text-white shadow-xl shadow-brand/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-600 hover:shadow-2xl hover:shadow-brand/40 active:translate-y-0"
            >
              <MessageCircle className="h-5 w-5" />
              Solicitar Orçamento Gratuito
            </a>
            <button
              data-testid="cta-hero-services"
              onClick={() => scrollToId("#servicos")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-7 py-4 text-base font-semibold text-navy transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40 hover:text-brand"
            >
              Ver Serviços
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="relative"
        >
          <div className="absolute -inset-3 -rotate-2 rounded-[2.2rem] bg-gradient-to-br from-brand/15 to-aqua/20" aria-hidden="true" />
          <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-navy/20">
            <img
              src={IMAGES.hero}
              alt="Profissional a realizar higienização de sofá com equipamento de extração"
              className="aspect-[4/5] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]"
              fetchPriority="high"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/30 via-transparent to-transparent" aria-hidden="true" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="glass-nav absolute -left-3 top-8 flex items-center gap-3 rounded-2xl border border-white/60 px-4 py-3 shadow-xl shadow-navy/10 sm:-left-6"
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-mist">
              <Leaf className="h-5 w-5 text-brand" />
            </span>
            <div>
              <p className="text-sm font-bold text-navy">Produtos ecológicos</p>
              <p className="text-xs text-slate-500">Limpeza responsável</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
            className="glass-nav absolute -right-2 bottom-8 flex items-center gap-3 rounded-2xl border border-white/60 px-4 py-3 shadow-xl shadow-navy/10 sm:-right-4"
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-mist">
              <Timer className="h-5 w-5 text-aqua" />
            </span>
            <div>
              <p className="text-sm font-bold text-navy">Secagem rápida</p>
              <p className="text-xs text-slate-500">Média de 10 a 12 horas</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
